import axios from 'axios';
import moment from 'moment-timezone';
import graphqlClient from '../../common/api/graphql';
import { DeleteSession, CreateBotMediaLibraryItem } from '../../common/queries/queries';
import api from '../../common/api/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import toast from 'react-hot-toast';

export const uploadToCloudinary = async (fileContent) => {
  const cloudName = process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME;
  const formData = new FormData();
  formData.append('file', fileContent);
  formData.append('upload_preset', 'bot_images');

  try {
    const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Error trying to upload image to cloudinary');
    }

    const data = await response.json();
    const secureUrl = data.url.replace(/^http:\/\//i, 'https://');
    return secureUrl;
  } catch (error) {
    console.error('Error trying to upload image to cloudinary', error);
    return null;
  }
};


export const sendWaTemplate = async (data, botId) => {
  try {
    const url = `webhook/${botId}/wa/template`;
    const resp = await api.post(url, data);
    if (resp.data) {
      return resp.data;
    }
  } catch (error) {
    // console.error('Error:', error);
    return null;
  }
  return null;
};

export const deleteSource = async (botId, docId) => {
  try {
    const url = `${botId}/documents/${docId}`;
    const resp = await api.delete(url);
    if (resp.data) {
      return resp.data;
    }
  } catch (error) {
    // console.error('Error:', error.response.data);
    return null;
  }
  return null;
};

export const personalityOptions = [
  { value: 'friendly', label: 'Amigable: Cálido, acogedor y utiliza un tono conversacional amigable.' },
  { value: 'professional', label: 'Profesional: Formal y serio, transmite confianza y conocimiento.' },
  {
    value: 'funny',
    label: 'Divertido: Utiliza un tono humorístico y hace uso de chistes o comentarios divertidos.',
  },
  { value: 'helpful', label: 'Servicial: Se enfoca en brindar ayuda y soluciones rápidas.' },
  { value: 'empathetic', label: 'Empático: Muestra comprensión y empatía hacia los usuarios.' },
  { value: 'elegant', label: 'Elegante: Utiliza un lenguaje sofisticado y refinado.' },
  {
    value: 'energetic',
    label: 'Enérgico: Entusiasta y motivador, transmite una sensación de energía y dinamismo.',
  },
  { value: 'neutral', label: 'Neutral: Mantiene un tono imparcial y objetivo.' },
  { value: 'creative', label: 'Creativo: Aporta ideas frescas y soluciones creativas.' },
  {
    value: 'expert',
    label: 'Experto: Se presenta como un conocedor en su área específica, transmite confianza en su conocimiento y capacidad para ayudar.',
  },
];

export function timeAgo(dateString) {
  const userTimeZone = moment.tz.guess();
  const now = moment().tz(userTimeZone);
  const past = moment.utc(dateString).tz(userTimeZone);

  const isToday = now.isSame(past, 'day');
  const isYesterday = now.subtract(1, 'day').isSame(past, 'day');

  if (isToday) {
    const hours = past.hour();
    const minutes = past.minute();
    return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
  } if (isYesterday) {
    return 'Ayer';
  }

  now.add(1, 'day');
  const daysAgo = now.diff(past, 'days');

  if (daysAgo === 1) {
    return `hace ${daysAgo} día`;
  }
  if (daysAgo < 7) {
    return `hace ${daysAgo} días`;
  }
  return past.format('DD/MM/YYYY');
}

export function capitalizeFirstLetter(string) {
  if (typeof string !== 'string' || !string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function extractWhatsappNumber(inputString, position = 1) {
  if (!inputString) return '';
  const parts = inputString.split('-');
  return parts[parts.length - position];
}

/**
 * A language is not a country. The Spanish here is neutral Latin American, so a Spanish
 * flag was telling our users they were reading someone else's dialect, and no single
 * flag covers the region anyway. Each label is written in its own language, which is
 * what a reader who cannot read the current one needs to find their way out.
 */
export const Languages = [
  { label: 'Español', value: 'es' },
  { label: 'English', value: 'en' },
  { label: 'Português', value: 'pt' },
];

export const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 'calc(100% - 32px)',
  maxWidth: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
};

// eslint-disable-next-line react/prop-types
export function TextError({ children }) {
  return <div style={{ color: 'red' }}>{children}</div>;
}

// Simple MIME type validation for file uploads
const validateFileByMime = (file, allowedTypes) => {
  if (!file || !file.type) {
    return { isValid: false, error: "Archivo no válido o sin tipo MIME" };
  }
  
  const isValid = allowedTypes.includes(file.type);
  return {
    isValid,
    error: isValid ? null : `Tipo de archivo no permitido: ${file.type}. Tipos permitidos: ${allowedTypes.join(", ")}`
  };
};


const getFileTypeFromMime = (mimeType) => {
  if (mimeType.startsWith('image/')) return 'IMAGE';
  if (mimeType.startsWith('video/')) return 'VIDEO';
  if (mimeType.startsWith('audio/')) return 'AUDIO';
  return 'DOCUMENT';
};

export const handleUploadFiles = async (file, bot_id, customFileName = null) => {
  if (!file) return null;
  if (customFileName && customFileName.trim() === '') {
    throw new Error('El nombre del archivo es obligatorio');
  }

  // Define allowed MIME types for file uploads
  const allowedMimeTypes = [
    // Videos
    "video/mp4", "video/quicktime", "video/x-msvideo",
    // Images
    "image/jpeg", "image/png", "image/gif", "image/webp",
    // Documents
    "application/pdf", "text/plain",
    // Audio
    "audio/mpeg", "audio/wav", "audio/ogg"
  ];

  // Simple MIME type validation
  const validation = validateFileByMime(file, allowedMimeTypes);
  if (!validation.isValid) {
    throw new Error(validation.error);
  }
  
  // The endpoint requires auth; useAuth sets this default on login.
  if (!axios.defaults.headers.common.Authorization) {
    throw new Error('Sesión no iniciada: vuelve a iniciar sesión para subir archivos');
  }

  try {
    let file_type = file.type;
    const BASE_URL = process.env.NEXT_PUBLIC_API_URL;
    const res = await axios.post(`${BASE_URL}/generate_presigned_url`, {
      file_type,
      bot_id,
      file_size: file.size,
    });
    if (res?.data) {
       const uploadFile = await axios.put(res.data.presigned_url, file, {
        headers: {
          'Content-Type': file_type,
        },
        transformRequest: (data, headers) => {
          delete headers['Authorization'];
          return data;
        }
      });
      if (uploadFile.status === 200) {
        // Save metadata to database if customFileName is provided
        if (customFileName && customFileName.trim()) {
          try {
            await graphqlClient.mutate({
              mutation: CreateBotMediaLibraryItem,
              variables: {
                item: {
                  bot_id,
                  file_name: customFileName.trim(),
                  file_url: res.data.public_url,
                  file_type: getFileTypeFromMime(file_type),
                  file_size: file.size,
                  mime_type: file_type
                }
              }
            });
          } catch (metadataError) {
            console.warn('Failed to save media metadata:', metadataError);
            // Don't fail the upload if metadata save fails
          }
        }
        
        return res.data.public_url;
      } else {
        toast.error('Error uploading file');
        return null;
      }
    }
    return null;
  } catch (error) {
    console.error('error', error);
    return error;
  }
};

export const MB = (size) => size * 1024 * 1024;

// Helper function to format file size
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export function capitalCase(string) {
  return string
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const cleanTextForPDF = (text) => {
  if (!text) return '';
  const cleaned = text
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1')
    .replace(/~(.*?)~/g, '$1');
  const emojiRegex = /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff]|\ufe0f)/g;
  return cleaned.replace(emojiRegex, '').trim();
};

export const deleteChatSession = async (chatId) => {
  const currentTimestamp = new Date().toISOString();
  await graphqlClient.mutate({
    mutation: DeleteSession,
    variables: { id: chatId, currentTimestamp },
  });
};

export const downloadChatAsPDF = ({ currentChat, messages, contactName, botName, t }) => {
  const doc = new jsPDF();
  const chatDate = moment(currentChat.created_at).format('DD-MM-YYYY');
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  doc.setFontSize(18);
  doc.text(t('chat_conversation_title'), 14, 22);

  doc.setFontSize(10);
  doc.setTextColor(150);
  doc.text(`${t('chat_timezone_disclaimer', 'Todas las horas se expresan en la zona horaria')}: ${userTimezone}`, 14, 30);
  doc.setTextColor(0);

  doc.setFontSize(12);
  doc.text(`${t('chat_creation_date')}: ${chatDate} - ${timeAgo(currentChat.created_at)}`, 14, 40);
  doc.text(`${t('chat_source')}: ${currentChat.source === 'whatsapp' ? t('chat_source_whatsapp') : t('chat_source_web')}`, 14, 46);
  if (currentChat.source === 'whatsapp') {
    doc.text(`${t('chat_recipient_number')}: ${currentChat.metadata?.recipient_number || ''}`, 14, 58);
  }

  const body = messages
    .filter(msg => msg.sender !== 'event')
    .map((msg) => {
      let senderName;
      if (msg.sender === 'user') {
        const c = msg.contact;
        if (c) {
          if (c.name) senderName = c.last_name ? `${c.name} ${c.last_name}` : c.name;
          else if (c.phone) senderName = `+${c.phone}`;
          else senderName = contactName;
        } else senderName = contactName;
      } else if (msg.sender === 'human' && msg.metadata?.sender_name) {
        senderName = msg.metadata.sender_name;
      } else if (msg.sender === 'human') {
        senderName = t('chat_human', 'Agente');
      } else {
        senderName = botName;
      }
      return [moment.utc(msg.created_at).local().format('DD/MM/YYYY HH:mm'), senderName, cleanTextForPDF(msg.text)];
    });

  doc.autoTable({
    head: [[t('chat_date_time', 'Fecha y Hora'), t('sender', 'Emisor'), t('chat_template')]],
    body,
    startY: 64,
    theme: 'grid',
    headStyles: { fillColor: [0, 57, 107] },
    styles: { cellPadding: 2, fontSize: 10, font: 'Helvetica' },
  });

  doc.save(`chat_${currentChat.id}.pdf`);
};

export const downloadChatAsJSON = ({ currentChat, messages, contactName, botName, t }) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Build export structure
  const exportData = {
    export_metadata: {
      export_date: new Date().toISOString(),
      chat_id: currentChat.id,
      bot_name: botName,
      contact_name: contactName,
      contact_phone: currentChat.contact?.phone || currentChat.metadata?.recipient_number || null,
      source: currentChat.source,
      chat_created_at: currentChat.created_at,
      timezone: userTimezone,
      total_items: messages.length,
      ai_enabled: currentChat.ai_enabled,
    },
    items: messages.map((item) => {
      // Format timestamp in user's local timezone
      const timestamp = moment.utc(item.created_at).local().format('YYYY-MM-DD HH:mm:ss');

      // Common fields
      const baseItem = {
        id: item.id,
        timestamp: timestamp,
        created_at_utc: item.created_at,
      };

      // Handle events
      if (item.sender === 'event') {
        return {
          ...baseItem,
          type: 'event',
          event_type: item.metadata?.event_type,
          event_category: item.metadata?.event_category,
          description: item.text,
          severity: item.metadata?.severity,
          created_by_type: item.metadata?.created_by_type,
          created_by_name: item.metadata?.created_by_name,
          event_metadata: item.metadata?.event_data?.metadata || item.metadata,
          full_event_data: item.metadata?.event_data,
        };
      }

      // Handle notes
      if (item.sender === 'note') {
        return {
          ...baseItem,
          type: 'note',
          content: item.text,
          note_type: item.metadata?.note_type,
          is_pinned: item.metadata?.is_pinned,
          created_by: item.metadata?.created_by,
          note_data: item.metadata?.note_data,
        };
      }

      // Handle regular messages
      let senderName;
      if (item.sender === 'user') {
        const c = item.contact;
        if (c) {
          if (c.name) senderName = c.last_name ? `${c.name} ${c.last_name}` : c.name;
          else if (c.phone) senderName = `+${c.phone}`;
          else senderName = contactName;
        } else senderName = contactName;
      } else if (item.sender === 'human' && item.metadata?.sender_name) {
        senderName = item.metadata.sender_name;
      } else if (item.sender === 'human') {
        senderName = t('chat_human', 'Agente');
      } else {
        senderName = botName;
      }

      return {
        ...baseItem,
        type: 'message',
        sender: item.sender,
        sender_name: senderName,
        text: item.text,
        metadata: item.metadata,
        ad_id: item.ad_id || null,
        message_feedback: item.message_feedback || [],
      };
    }),
  };

  // Create downloadable file
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  const timestamp = moment().format('YYYY-MM-DD_HH-mm-ss');
  link.download = `chat_export_${currentChat.id}_${timestamp}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadFeedbackSessionAsJSON = ({ sessionData, feedback, botName, t }) => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Get contact name
  const contact = sessionData.contact;
  let contactName = t('chat_user', 'User');
  if (contact?.name) {
    contactName = contact.last_name ? `${contact.name} ${contact.last_name}` : contact.name;
  } else if (contact?.phone) {
    contactName = `+${contact.phone}`;
  }

  // Combine messages and events, sorted by date
  const allItems = [
    ...(sessionData.messages || []).map(msg => ({ ...msg, _type: 'message' })),
    ...(sessionData.bot_session_events || []).map(evt => ({ ...evt, _type: 'event', sender: 'event' })),
  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  const exportData = {
    export_metadata: {
      export_date: new Date().toISOString(),
      export_type: 'feedback_session',
      session_id: sessionData.id,
      feedback_id: feedback.id,
      bot_name: botName,
      contact_name: contactName,
      contact_phone: contact?.phone || null,
      source: sessionData.source,
      session_created_at: sessionData.created_at,
      timezone: userTimezone,
      total_items: allItems.length,
    },
    feedback: {
      id: feedback.id,
      message_id: feedback.message_id,
      created_at: feedback.created_at,
      client_feedback: {
        comment: feedback.comment,
        expected_response: feedback.expected_response,
      },
      analyst_review: {
        analysis: feedback.analysis,
        correction: feedback.correction,
        correction_status: feedback.correction_status,
        priority: feedback.priority,
        category: feedback.category,
        notes: feedback.notes,
        analyzed_by: feedback.analyzed_by,
        analyzed_at: feedback.analyzed_at,
        applied_at: feedback.applied_at,
      }
    },
    items: allItems.map((item) => {
      const timestamp = moment.utc(item.created_at).local().format('YYYY-MM-DD HH:mm:ss');

      // Handle events
      if (item._type === 'event' || item.sender === 'event') {
        return {
          id: item.id,
          timestamp,
          created_at_utc: item.created_at,
          type: 'event',
          event_type: item.event_type || item.metadata?.event_type,
          event_category: item.event_category || item.metadata?.event_category,
          description: item.description || item.text,
          severity: item.severity || item.metadata?.severity,
          created_by_type: item.created_by_type || item.metadata?.created_by_type,
          created_by_name: item.created_by_name || item.metadata?.created_by_name,
          metadata: item.metadata,
        };
      }

      // Handle messages
      let senderName = botName;
      if (item.sender === 'user') {
        senderName = contactName;
      } else if (item.sender === 'human') {
        senderName = item.metadata?.sender_name || t('chat_human', 'Agente');
      }

      return {
        id: item.id,
        timestamp,
        created_at_utc: item.created_at,
        type: 'message',
        sender: item.sender,
        sender_name: senderName,
        text: item.text,
        metadata: item.metadata,
        is_feedback_target: item.id === feedback.message_id,
      };
    }),
  };

  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  const ts = moment().format('YYYY-MM-DD_HH-mm-ss');
  link.download = `feedback_${feedback.id.slice(0, 8)}_${ts}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const sanitizeSearchInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/\\/g, '\\\\\\\\') 
    .replace(/%/g, '\\%')       
    .replace(/_/g, '\\_')       
    .replace(/\[/g, '\\[')      
    .replace(/\]/g, '\\]')      
    .replace(/'/g, "''")        
    .trim();
};

export const sanitizeTextInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') 
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') 
    .replace(/javascript:/gi, '') 
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '') 
    .replace(/on\w+\s*=\s*[^>\s]+/gi, '') 
    .replace(/data:(?!image\/[a-z]+;base64,)[^;,\s]*/gi, '');
};

export const validateVariableName = (variable) => {
  if (!variable || typeof variable !== 'string') return false;
  
  const validVariableRegex = /^[a-zA-Z_][a-zA-Z0-9_\.]*$/;
  
  if (variable.length > 50) return false;
  
  return validVariableRegex.test(variable);
};

export const sanitizeVariableValue = (value) => {
  if (!value || typeof value !== 'string') return '';
  
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

export const updateVariableSequence = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  const regex = /{{\d+}}/g;
  const matches = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push(match);
  }

  let updatedText = text;
  matches.forEach((match, index) => {
    updatedText = updatedText.replace(match[0], `{{${index + 1}}}`);
  });

  return updatedText;
};

export const getNextVariableIndex = (text) => {
  if (!text || typeof text !== 'string') return 1;
  
  const regex = /{{(\d+)}}/g;
  const matches = [];
  let match;

  while ((match = regex.exec(text)) !== null) {
    matches.push(parseInt(match[1], 10));
  }

  if (matches.length > 0) {
    return Math.max(...matches) + 1;
  } else {
    return 1;
  }
};

export const insertVariableAtCursor = (textAreaRef, currentText, setText, updateSequenceCallback) => {
  const textarea = textAreaRef.current;
  if (!textarea) return null;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;

  const nextIndex = getNextVariableIndex(currentText);
  const variable = `{{${nextIndex}}}`;

  const newText = currentText.slice(0, start) + variable + currentText.slice(end);
  const updatedText = updateSequenceCallback ? updateSequenceCallback(newText) : updateVariableSequence(newText);
  setText(updatedText);

  return setTimeout(() => {
    if (textarea && textarea.focus && textarea.setSelectionRange) {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }
  }, 0);
};

export const insertFormattingAtCursor = (textAreaRef, currentText, setText, before, after = before) => {
  const textarea = textAreaRef.current;
  if (!textarea) return null;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = currentText.slice(start, end);
  const newText = currentText.slice(0, start) + before + selectedText + after + currentText.slice(end);

  setText(newText);

  return setTimeout(() => {
    if (textarea && textarea.focus && textarea.setSelectionRange) {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selectedText.length
      );
    }
  }, 0);
};

